import * as React from "react";
import {
  Badge,
  IconButton,
  Popover,
  List,
  ListItemButton,
  ListItemText,
  Typography,
  Chip,
  Divider,
  Button,
  Box,
  CircularProgress,
  Tabs,
  Tab,
  Tooltip,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import {
  Notifications as NotificationsIcon,
  ChevronRight as ChevronRightIcon,
} from "@mui/icons-material";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { useUser } from "../../contexts/UserProvider";
import useNotifications, { NOTIFICATIONS_PAGE_SIZE } from "../../hooks/useNotifications";
import { useTokens } from "../../theme";
import { formatAge } from "../RefreshStatus";

// Darkened so the white chip text clears the 4.5:1 WCAG 2.2 minimum.
const PRIORITY_COLOR = {
  URGENT: "#b91c1c",
  LOW: "#0e7490",
};

const AUDIENCE_LABEL = {
  INDIVIDUAL_AGENT: "For you",
  SUBORDINATE_AGENTS: "For your team",
  SUBORDINATE_GROUP_AGENTS: "For your group",
  ALL_AGENTS: "For all agents",
  SALES_MANAGER: "For your sales manager",
  GROUP_MANAGER: "For your group manager",
  GROUP_ADMIN: "For group admins",
  ADMIN: "For admins",
};

// Maps a notification's eventType to a path builder. Notifications whose
// eventType isn't listed here are marked-read on click but don't navigate.
// Each builder also seeds the destination table's filter (via query params
// matching that dashboard's DEFAULT_FILTER keys, same mechanism GlobalSearch.js
// uses) from notification.data, so the click lands on the specific record
// instead of the whole unfiltered table. notification.data is a free-form
// Map<String, Object> - older, already-dispatched notifications may not carry
// these fields, so every field read here is optional.
function withParams(base, params) {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      searchParams.set(key, value);
    }
  });
  const queryString = searchParams.toString();
  return queryString ? `${base}?${queryString}` : base;
}

function clientRoute(notification) {
  return withParams("/clientDashboard", {
    id: notification.agentId,
    firstName: notification.data?.firstName,
    lastName: notification.data?.lastName,
  });
}

function groupsTabRoute(notification) {
  return withParams("/clientDashboard", {
    id: notification.agentId,
    tab: "groups",
    name: notification.data?.groupName,
  });
}

const CLICKABLE_EVENT_ROUTES = {
  ANCILLARY_POLICY_CREATED: (notification) => clientRoute(notification),
  GROUP_POLICY_CREATED: (notification) => groupsTabRoute(notification),
  GROUP_POLICY_ASSIGNED: (notification) => groupsTabRoute(notification),
  GROUP_POLICY_EDITED: (notification) => groupsTabRoute(notification),
  GROUP_POLICY_SUBMITTED_CHANGED: (notification) => groupsTabRoute(notification),
  GROUP_POLICY_STATUS_CHANGED: (notification) => groupsTabRoute(notification),
  GROUP_AGENT_CHANGED: (notification) => groupsTabRoute(notification),
  GROUP_POLICY_RENEWAL_REMINDER: (notification) => groupsTabRoute(notification),
  // adminId is intentionally omitted: the reassigned-to agent may not hold an
  // ADMIN/GROUP_MANAGER role, and the admin/all endpoint requires the scoping
  // id to have one. Defaulting to the viewing admin's own id keeps the request
  // authorized; agentName filters the table down to the reassigned agent.
  GROUP_REASSIGNED: (notification) =>
    withParams("/groupDashboard", { agentName: notification.data?.agentName }),
  CLIENT_POLICY_EDITED: (notification) => clientRoute(notification),
  CLIENT_LEAD_CONVERTED: (notification) => clientRoute(notification),
  CLIENT_REASSIGNED: (notification) =>
    withParams("/clientDashboard", { id: notification.agentId }),
  // adminId is intentionally omitted here too, for the same reason as
  // GROUP_REASSIGNED: notification.agentId is the agent whose role changed,
  // who may not be an admin themselves.
  AGENT_ROLE_UPDATED: (notification) =>
    withParams("/adminTable", {
      tab: "agents",
      firstName: notification.data?.firstName,
      lastName: notification.data?.lastName,
    }),
};

// "2h ago" for the last day, then the date; the exact time is in a tooltip.
function relativeTime(createdAt) {
  const when = dayjs(createdAt);
  if (!when.isValid()) return "";
  return dayjs().diff(when, "hour") < 24 ? formatAge(when.valueOf()) : when.format("MMM D");
}

const PRIORITY_TABS = [
  { label: "All", value: "ALL" },
  { label: "Urgent", value: "URGENT" },
  { label: "Low", value: "LOW" },
];

export default function NotificationBell() {
  const { id } = useUser();
  const tokens = useTokens();
  const [limit, setLimit] = React.useState(NOTIFICATIONS_PAGE_SIZE);
  const { notifications, unreadCount, totalCount, isLoading, markRead, markAllRead } =
    useNotifications(id, limit);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [priorityFilter, setPriorityFilter] = React.useState("ALL");
  const navigate = useNavigate();
  const theme = useTheme();
  // Hardcoding a light-mode-only tint here made unread rows nearly
  // invisible in dark mode (near-white text on a near-white background),
  // legible only once the hover overlay darkened it. Blend a low-alpha
  // primary tint instead so it stays readable in both modes.
  const unreadBg = alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.16 : 0.08);

  const open = Boolean(anchorEl);
  const popoverId = "notification-bell-popover";

  const handleOpen = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const filteredNotifications =
    priorityFilter === "ALL"
      ? notifications
      : notifications.filter((n) => n.priority === priorityFilter);

  const handleItemClick = (notification) => {
    if (!notification.readBy?.includes(id)) {
      markRead(notification.id);
    }

    const buildRoute = CLICKABLE_EVENT_ROUTES[notification.eventType];

    if (buildRoute) {
      navigate(buildRoute(notification));
      handleClose();
    }
  };

  return (
    <>
      <IconButton
        onClick={handleOpen}
        sx={{ color: tokens.sidebarForegroundActive }}
        aria-label={
          unreadCount > 0
            ? `Notifications, ${unreadCount} unread`
            : "Notifications"
        }
        aria-haspopup="true"
        aria-expanded={open ? "true" : "false"}
        aria-controls={open ? popoverId : undefined}
      >
        <Badge badgeContent={unreadCount} color="error">
          <NotificationsIcon />
        </Badge>
      </IconButton>
      <Popover
        id={popoverId}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Box
          sx={{
            width: 360,
            maxHeight: 420,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "10px 16px",
            }}
          >
            <Typography sx={{ fontWeight: "bold" }}>Notifications</Typography>
            {unreadCount > 0 && (
              <Button size="small" onClick={() => markAllRead()}>
                Mark all as read
              </Button>
            )}
          </Box>
          <Tabs
            value={priorityFilter}
            onChange={(_event, newValue) => setPriorityFilter(newValue)}
            variant="fullWidth"
            sx={{ minHeight: 36 }}
          >
            {PRIORITY_TABS.map((tab) => (
              <Tab
                key={tab.value}
                value={tab.value}
                label={tab.label}
                sx={{ minHeight: 36, py: 0 }}
              />
            ))}
          </Tabs>
          <Divider />
          {isLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", padding: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : filteredNotifications.length === 0 ? (
            <Typography
              sx={{ padding: 3, textAlign: "center", color: "text.secondary" }}
            >
              {priorityFilter === "ALL"
                ? "You're all caught up"
                : `No ${priorityFilter.toLowerCase()} priority notifications`}
            </Typography>
          ) : (
            <List sx={{ overflowY: "auto" }}>
              {filteredNotifications.map((notification) => {
                const isRead = notification.readBy?.includes(id);
                const isClickable = Boolean(CLICKABLE_EVENT_ROUTES[notification.eventType]);

                return (
                  <ListItemButton
                    key={notification.id}
                    onClick={() => handleItemClick(notification)}
                    sx={{
                      alignItems: "flex-start",
                      bgcolor: isRead ? "transparent" : unreadBg,
                      cursor: isClickable ? "pointer" : "default",
                    }}
                  >
                    <ListItemText
                      primary={
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Chip
                            size="small"
                            label={notification.priority}
                            sx={{
                              bgcolor: PRIORITY_COLOR[notification.priority] || "#475569",
                              color: "white",
                              fontWeight: "bold",
                            }}
                          />
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: isRead ? "normal" : "bold" }}
                          >
                            {notification.data?.message ||
                              AUDIENCE_LABEL[notification.audienceLevel] ||
                              "Notification"}
                          </Typography>
                        </Box>
                      }
                      secondary={
                        notification.createdAt ? (
                          <Tooltip title={dayjs(notification.createdAt).format("MMM D, YYYY h:mm A")}>
                            <span>{relativeTime(notification.createdAt)}</span>
                          </Tooltip>
                        ) : (
                          ""
                        )
                      }
                    />
                    {isClickable && (
                      <ChevronRightIcon
                        fontSize="small"
                        sx={{ color: "text.secondary", marginLeft: 1, flexShrink: 0 }}
                      />
                    )}
                  </ListItemButton>
                );
              })}
              {notifications.length < (totalCount || 0) && (
                <Box sx={{ display: "flex", justifyContent: "center", padding: 1 }}>
                  <Button
                    size="small"
                    onClick={() => setLimit((prev) => prev + NOTIFICATIONS_PAGE_SIZE)}
                  >
                    Load more
                  </Button>
                </Box>
              )}
            </List>
          )}
        </Box>
      </Popover>
    </>
  );
}
